import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import { COMPANY, scrollToId } from "@/lib/site";

const LINKS = [
  { id: "about", label: "About" },
  { id: "nomnom", label: "NomNom" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (onHome) scrollToId(id);
    else window.location.href = `/#${id}`;
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 px-4 pt-3 sm:px-6">
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 rounded-full border border-nom-border bg-white/80 py-2 pl-4 pr-2 shadow-nom backdrop-blur-xl sm:pl-5">
        <Logo />
        <div className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`/#${l.id}`}
              onClick={go(l.id)}
              data-testid={`nav-${l.id}-link`}
              className="rounded-full px-4 py-2 text-sm font-bold text-nom-inkSoft transition-colors duration-200 hover:bg-nom-peach/60 hover:text-nom-ink"
            >
              {l.label}
            </a>
          ))}
          <a
            href={`mailto:${COMPANY.email}`}
            data-testid="nav-email-button"
            className="ml-2 inline-flex items-center gap-2 rounded-full bg-nom-coral px-5 py-2.5 text-sm font-extrabold text-white shadow-coral transition-all duration-200 hover:-translate-y-0.5 hover:bg-nom-coralDark active:scale-[0.98]"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Email us
          </a>
        </div>
        <button
          className="inline-flex h-10 w-10 items-center justify-center rounded-full text-nom-ink transition-colors hover:bg-nom-peach/60 md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-button"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="mx-auto mt-2 max-w-6xl rounded-3xl border border-nom-border bg-white/95 p-3 shadow-nomLg backdrop-blur-xl md:hidden"
            data-testid="mobile-menu-panel"
          >
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={go(l.id)}
                data-testid={`mobile-nav-${l.id}-link`}
                className="block rounded-2xl px-4 py-3 font-display text-lg font-medium text-nom-ink transition-colors hover:bg-nom-peach/60"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="mobile-nav-email-button"
              className="mt-2 flex items-center justify-center gap-2 rounded-2xl bg-nom-coral px-4 py-3 font-display text-lg font-medium text-white"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              Email us
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
